import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../../context/CartContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const productGradients = [
    'from-indigo-950/50 to-zinc-900',
    'from-zinc-800 to-zinc-900',
    'from-amber-950/30 to-zinc-900',
    'from-emerald-950/30 to-zinc-900',
    'from-rose-950/30 to-zinc-900',
    'from-violet-950/30 to-zinc-900',
];

const ProductCard = ({ product, index }) => {
    const [wishlisted, setWishlisted] = useState(false);
    const { addToCart } = useCart();
    const gradient = productGradients[index % productGradients.length];

    const discount = product.original_price 
        ? Math.round((1 - product.price / product.original_price) * 100) 
        : 0;

    const handleAddToCart = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const size = product.sizes?.[0] || null;
        await addToCart(product, 1, size);
        toast.success('Added to cart!');
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.05 }}
        >
            <Link
                to={`/products/${product.id}`}
                className="group block bg-card border border-border/50 hover:border-lime/50 transition-colors overflow-hidden"
                data-testid={`product-card-${product.id}`}
            >
                {/* Image */}
                <div className="relative aspect-[3/4] overflow-hidden">
                    <div className={`absolute inset-0 bg-gradient-to-br ${gradient}`} />
                    
                    {/* Placeholder visual */}
                    <div className="absolute inset-0 flex items-center justify-center group-hover:scale-105 transition-transform duration-500">
                        {product.image_url ? (
                            <img 
                                src={product.image_url} 
                                alt={product.name}
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <span className="text-6xl opacity-50">
                                {product.category === 'tshirts' ? '👕' : 
                                 product.category === 'hoodies' ? '🧥' :
                                 product.category === 'bottoms' ? '👖' :
                                 product.category === 'shirts' ? '👔' : '🎒'}
                            </span>
                        )}
                    </div>

                    {/* Badge */}
                    {product.badge && (
                        <div 
                            className={`absolute top-3 left-3 px-2 py-1 text-[9px] font-bold tracking-[0.1em] uppercase ${
                                product.badge === 'SALE' ? 'bg-rose-500 text-white' :
                                product.badge === 'HOT' ? 'bg-lime text-black' :
                                'bg-white text-black'
                            }`}
                        >
                            {product.badge}
                        </div>
                    )}

                    {/* Wishlist */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setWishlisted(!wishlisted);
                        }}
                        className={`absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full transition-colors ${
                            wishlisted ? 'bg-lime text-black' : 'bg-black/50 text-white hover:bg-lime hover:text-black'
                        }`}
                        data-testid={`wishlist-${product.id}`}
                    >
                        <Heart className={`w-4 h-4 ${wishlisted ? 'fill-current' : ''}`} />
                    </button>

                    {/* Quick add */}
                    <button
                        onClick={handleAddToCart}
                        className="absolute bottom-0 left-0 right-0 bg-lime text-black py-3 text-xs font-bold tracking-[0.1em] uppercase opacity-0 group-hover:opacity-100 translate-y-full group-hover:translate-y-0 transition-all duration-300"
                        data-testid={`quick-add-${product.id}`}
                    >
                        QUICK ADD
                    </button>
                </div>

                {/* Info */}
                <div className="p-3 md:p-4">
                    <h3 className="text-sm font-medium leading-tight mb-2 truncate group-hover:text-lime transition-colors">
                        {product.name}
                    </h3>
                    <div className="flex items-center gap-2">
                        <span className="font-bold">₹{product.price.toLocaleString()}</span>
                        {product.original_price && (
                            <>
                                <span className="text-sm text-muted-foreground line-through">
                                    ₹{product.original_price.toLocaleString()}
                                </span>
                                <span className="text-xs font-bold text-lime">
                                    {discount}% OFF
                                </span>
                            </>
                        )}
                    </div>
                    {product.sizes && product.sizes.length > 0 && (
                        <div className="flex gap-1 mt-2">
                            {product.sizes.slice(0, 4).map((size) => (
                                <span 
                                    key={size} 
                                    className="text-[9px] text-muted-foreground border border-border px-1.5 py-0.5"
                                >
                                    {size}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </Link>
        </motion.div>
    );
};

export const Products = () => {
    const [products, setProducts] = useState([]);
    const [activeFilter, setActiveFilter] = useState('all');
    const [loading, setLoading] = useState(true);

    const filters = [
        { id: 'all', name: 'ALL' },
        { id: 'tshirts', name: 'T-SHIRTS' },
        { id: 'hoodies', name: 'HOODIES' },
    ];

    useEffect(() => {
        const fetchProducts = async () => {
            setLoading(true);
            try {
                const url = activeFilter === 'all' 
                    ? `${API}/products` 
                    : `${API}/products?category=${activeFilter}`;
                const response = await axios.get(url);
                setProducts(response.data);
            } catch (error) {
                console.error('Error fetching products:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchProducts();
    }, [activeFilter]);

    return (
        <section className="py-16 md:py-24" data-testid="products-section">
            <div className="max-w-[1400px] mx-auto px-4 md:px-8">
                {/* Header */}
                <div className="flex justify-between items-end mb-8">
                    <div>
                        <span className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2 block">
                            WHAT'S HOT
                        </span>
                        <h2 className="font-heading text-4xl md:text-6xl">
                            NEW<br />ARRIVALS
                        </h2>
                    </div>
                    <Link
                        to="/products"
                        className="text-xs font-medium tracking-[0.1em] uppercase text-lime border-b border-lime pb-1 hover:tracking-[0.15em] transition-all hidden md:block"
                    >
                        ALL PRODUCTS →
                    </Link>
                </div>

                {/* Filters */}
                <div className="flex gap-2 overflow-x-auto pb-4 mb-8 scrollbar-hide" data-testid="product-filters">
                    {filters.map((filter) => (
                        <button
                            key={filter.id}
                            onClick={() => setActiveFilter(filter.id)}
                            className={`whitespace-nowrap text-xs font-semibold tracking-[0.1em] uppercase px-4 py-2 border transition-all ${
                                activeFilter === filter.id
                                    ? 'bg-lime text-black border-lime'
                                    : 'border-border text-muted-foreground hover:border-white hover:text-white'
                            }`}
                            data-testid={`filter-${filter.id}`}
                        >
                            {filter.name}
                        </button>
                    ))}
                </div>

                {/* Grid */}
                {loading ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {[...Array(8)].map((_, i) => (
                            <div key={i} className="aspect-[3/4] bg-card animate-pulse" />
                        ))}
                    </div>
                ) : products.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {products.map((product, index) => (
                            <ProductCard key={product.id} product={product} index={index} />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12 text-muted-foreground">
                        No products found. Check back soon!
                    </div>
                )}
            </div>
        </section>
    );
};
